// Pure drag-drop helpers for cards/parent/StudyPlanEdit. The page
// renders two parallel lesson lists ("lesson1" and "lesson2") for the
// side-by-side preview. Both helpers take the current state of both
// lists plus the react-beautiful-dnd drop ``result`` and return the
// new state for both lists. No mutation of inputs — the inline
// versions in StudyPlanEdit used `.splice` on closure-captured arrays
// which mutated state in place; that was technically wrong even
// though the subsequent setState(spread) hid the symptom under
// React 17 batching. Pure helpers fix the latent bug without
// changing any visible behaviour.
//
// The drop ``result`` shape matches react-beautiful-dnd:
//   { source: { droppableId, index }, destination: { droppableId, index } | null }
// For topics, droppableId is "<topics>:<lessonIndex>".

function _pickLessonList(droppableId, lessons1, lessons2) {
    return droppableId === 'lesson1' ? lessons1 : lessons2;
}

function _withLessonList(droppableId, newList, lessons1, lessons2) {
    return droppableId === 'lesson1'
        ? { lessons1: newList, lessons2 }
        : { lessons1, lessons2: newList };
}

export function applyLessonDrag(result, lessons1, lessons2) {
    const { source, destination } = result;

    // Same-list reorder (or drop-outside, which removes the lesson).
    if (!destination || source.droppableId === destination.droppableId) {
        const src = [..._pickLessonList(source.droppableId, lessons1, lessons2)];
        const [removed] = src.splice(source.index, 1);
        if (destination) {
            src.splice(destination.index, 0, removed);
        }
        return _pickLessonList(source.droppableId, lessons1, lessons2) === lessons1
            ? { lessons1: src, lessons2 }
            : { lessons1, lessons2: src };
    }

    // Cross-list move.
    const srcArr = [..._pickLessonList(source.droppableId, lessons1, lessons2)];
    const dstArr = [..._pickLessonList(destination.droppableId, lessons1, lessons2)];
    const [removed] = srcArr.splice(source.index, 1);
    dstArr.splice(destination.index, 0, removed);

    if (source.droppableId === 'lesson1') {
        return { lessons1: srcArr, lessons2: dstArr };
    }
    return { lessons1: dstArr, lessons2: srcArr };
}

export function applyTopicDrag(result, lessons1, lessons2) {
    const { source, destination } = result;
    const sourceListId = source.droppableId.split(':')[0];
    const sourceLessonIndex = parseInt(source.droppableId.split(':')[1], 10);
    const sourceTopicIndex = source.index;

    // Drop-outside: remove the topic.
    if (!destination) {
        const list = [..._pickLessonList(sourceListId, lessons1, lessons2)];
        const lesson = { ...list[sourceLessonIndex] };
        lesson.topics = [...lesson.topics];
        lesson.topics.splice(sourceTopicIndex, 1);
        list[sourceLessonIndex] = lesson;
        return _withLessonList(sourceListId, list, lessons1, lessons2);
    }

    const destListId = destination.droppableId.split(':')[0];
    const destLessonIndex = parseInt(destination.droppableId.split(':')[1], 10);
    const destTopicIndex = destination.index;

    const sameLesson =
        sourceListId === destListId && sourceLessonIndex === destLessonIndex;

    if (sameLesson) {
        const list = [..._pickLessonList(sourceListId, lessons1, lessons2)];
        const lesson = { ...list[sourceLessonIndex] };
        lesson.topics = [...lesson.topics];
        const [moved] = lesson.topics.splice(sourceTopicIndex, 1);
        lesson.topics.splice(destTopicIndex, 0, moved);
        list[sourceLessonIndex] = lesson;
        return _withLessonList(sourceListId, list, lessons1, lessons2);
    }

    // Cross-lesson move. Could be within the same list or across lists.
    let next1 = lessons1;
    let next2 = lessons2;

    const srcList = [..._pickLessonList(sourceListId, next1, next2)];
    const srcLesson = { ...srcList[sourceLessonIndex] };
    srcLesson.topics = [...srcLesson.topics];
    const [moved] = srcLesson.topics.splice(sourceTopicIndex, 1);
    srcList[sourceLessonIndex] = srcLesson;

    if (sourceListId === 'lesson1') {
        next1 = srcList;
    } else {
        next2 = srcList;
    }

    const dstList = [..._pickLessonList(destListId, next1, next2)];
    const dstLesson = { ...dstList[destLessonIndex] };
    dstLesson.topics = [...dstLesson.topics];
    dstLesson.topics.splice(destTopicIndex, 0, moved);
    dstList[destLessonIndex] = dstLesson;

    if (destListId === 'lesson1') {
        next1 = dstList;
    } else {
        next2 = dstList;
    }

    return { lessons1: next1, lessons2: next2 };
}
